import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../api/api";
import { APIError } from "../../../constants/types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { showMessage } from "../../messages/messagesActions";
import { INITIAL_RISK_PROFILE, IRiskProfile } from "./commAutoRiskProfile.types";

export const fetchRiskProfileListAsync = createAsyncThunk<
  { data: IRiskProfile[] },
  { customerPolicyId: number; status: string },
  { rejectValue: APIError }
>(
  "riskProfile/fetchList",
  async ({ customerPolicyId, status }, { rejectWithValue }) => {
    try {
      let url = `/policy/get-policy-commauto-risk-profile?customer_policy_id=${customerPolicyId}`;
      if (status !== "-1") {
        url += `&status=${status}`;
      }
      const res = await api.get(url);
      const data: IRiskProfile[] = res.data.data;
      return { data };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchRiskProfileAsync = createAsyncThunk<
  { data: IRiskProfile },
  { customerPolicyId: number; incrementalNumber: number },
  { rejectValue: APIError }
>(
  "riskProfile/fetch",
  async ({ customerPolicyId, incrementalNumber }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/policy/get-policy-commauto-risk-profile?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}`,
      );
      const data: IRiskProfile[] = res.data.data;
      return { data: data.length > 0 ? data[0] : INITIAL_RISK_PROFILE };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const saveRiskProfileAsync = createAsyncThunk<
  { data: IRiskProfile },
  { data: IRiskProfile; onCallback: (isSuccess: boolean, data?: IRiskProfile) => void },
  { rejectValue: APIError }
>(
  "riskProfile/save",
  async ({ data, onCallback }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/policy/edit-policy-commauto-risk-profile", {
        ...data,
      });
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Risk Profile saved successfully!",
          displayAs: "snackbar",
        }),
      );
      return { data: res.data.data };
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        }),
      );
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";

interface IConvertToMarketingSubmissionPayload {
  customer_policy_id: number;
  customer_id: number;
  incremental_number: number | null;
  sections: {
    brokerInsuredPolicyInfo: boolean;
    operationsRiskDetails: boolean;
    coverageRequested: boolean;
    driverSchedule: boolean;
    vehicleSchedule: boolean;
    claimsConvictionHistory: boolean;
    riskManagementQuestionnaire: boolean;
    premisesRequiredDocuments: boolean;
    declarationsSignatures: boolean;
  };
}

export interface IRiskProfileMarketSubmissionSection {
  name: string;
}

export const generateRiskProfileMarketSubmissionPDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    sections: IRiskProfileMarketSubmissionSection[],
    onCallback: (isSuccess: boolean, pdfBlob?: Blob) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        "/policy/get-policy-commauto-risk-profile-market-submission-pdf",
        {
          customer_policy_id: customerPolicyId,
          incremental_number: incrementalNumber,
          sections,
        },
        {
          responseType: "arraybuffer",
        }
      );
      const pdfBlob = new Blob([res.data], { type: "application/pdf" });
      onCallback(true, pdfBlob);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const convertRiskProfileToMarketingSubmissionAsync =
  (
    payload: IConvertToMarketingSubmissionPayload,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      // await api.post(
      //   "/policy/convert-commauto-risk-profile-to-marketing-submission",
      //   payload
      // );
      dispatch(
        showMessage({
          type: "success",
          message: "Risk Profile converted to marketing submission successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const saveRiskProfileMarketingHistoryAsync =
  (
    data: any,
    customerId: number,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      await api.post(
        "/policy/edit-policy-commauto-add-marketing-history",
        data
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Marketing history saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const downloadRiskProfilePDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/policy/get-policy-commauto-risk-profile-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `risk_profile_${incrementalNumber}.pdf`;
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };

export const previewRiskProfilePDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/policy/get-policy-commauto-risk-profile-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&is_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };

export const downloadRiskProfileMarketSubmissionPDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `/policy/get-policy-commauto-risk-profile-market-submission-pdf`,
        {
          customer_policy_id: customerPolicyId,
          incremental_number: incrementalNumber,
        },
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `risk_profile_market_submission_${incrementalNumber}.pdf`;
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };

export const previewRiskProfileMarketSubmissionPDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `/policy/get-policy-commauto-risk-profile-market-submission-pdf`,
        {
          customer_policy_id: customerPolicyId,
          incremental_number: incrementalNumber,
          is_policy_commauto_risk_profile_preview: true,
        }
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };
